import type React from 'react';
import { useState } from 'react';

import { useStyles } from 'components/alert/showOnce/styles';
import TMXAlert, { type TMXAlertProps } from 'components/alert/TMXAlert';

interface Props extends TMXAlertProps {
  storageName: string;
  isShown?: () => boolean;
  perTournament?: boolean;
  tournamentId?: string;
  dismissable?: boolean;
}

const TMXShowOnceAlert: React.FC<React.PropsWithChildren<Props>> = ({
  children,
  storageName,
  isShown,
  perTournament = false,
  tournamentId,
  dismissable = true,
  ...props
}) => {
  const { classes } = useStyles();

  const findStorageValue = (tournamentId) => {
    let storeValue: string | Record<string, unknown> | null = localStorage.getItem(storageName);

    if (storeValue !== null) {
      if (tournamentId && perTournament) {
        storeValue = JSON.parse(storeValue);
      }
    }
    return storeValue;
  };

  const setStorageValue = (tournamentId, value) => {
    const storeValue = localStorage.getItem(storageName);

    if (perTournament && tournamentId) {
      const newValue: Record<string, unknown> | null = JSON.parse(storeValue);

      newValue[tournamentId] = value;

      localStorage.setItem(storageName, JSON.stringify(newValue));
    } else {
      localStorage.setItem(storageName, value);
    }
  };

  const initialState = () => {
    const storageValue: string | Record<string, unknown> | null = findStorageValue(tournamentId);
    let returnedValue: boolean;

    if (storageValue === null) {
      if (perTournament && tournamentId) {
        const newByTournamentObj: Record<string, unknown> = {};
        newByTournamentObj[tournamentId] = 'true';
        localStorage.setItem(storageName, JSON.stringify(newByTournamentObj));
      } else {
        localStorage.setItem(storageName, 'true');
      }
      returnedValue = true;
    } else {
      if (perTournament && tournamentId) {
        if (typeof storageValue[tournamentId] === 'undefined') {
          setStorageValue(tournamentId, 'true');
          returnedValue = true;
        } else {
          returnedValue = storageValue[tournamentId] === 'true';
        }
      } else {
        returnedValue = storageValue === 'true';
      }
    }

    const showFuncRes: boolean = isShown ? isShown() : true;

    return returnedValue === true && showFuncRes === true;
  };

  const [showAlert, setShowAlert] = useState<boolean>(initialState());

  const dismissAlert = () => {
    setStorageValue(tournamentId, 'false');
    setShowAlert(false);

    if (props.onClose) {
      props.onClose();
    }
  };

  return (
    showAlert && (
      <TMXAlert
        className={`${classes.pageAlert} ${props.className}`}
        dismissable={dismissable}
        onClose={dismissAlert}
        {...props}
      >
        {children}
      </TMXAlert>
    )
  );
};

export default TMXShowOnceAlert;
